import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Tooltip,
    Container
} from '@mui/material';
import { DateTime } from 'luxon';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const SyncHistory = () => {
    const navigate = useNavigate();
    const [history, setHistory] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/admin/sync/history?page=${page + 1}&limit=${rowsPerPage}`);
            setHistory(response.data.data);
            setTotal(response.data.pagination.total);
        } catch (error) {
            console.log('Error fetching sync history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, [page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDate = (date) => {
        return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_SHORT);
    };

    const formatDuration = (startTime, endTime) => {
        const start = DateTime.fromISO(startTime);
        const end = DateTime.fromISO(endTime);
        const diff = end.diff(start, ['minutes', 'seconds']).toObject();
        return `${Math.floor(diff.minutes)}m ${Math.floor(diff.seconds)}s`;
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 3, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Tooltip title="Back to Dashboard">
                        <IconButton 
                            onClick={() => navigate('/dashboard')}
                            size="small"
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h5" component="h1">
                        Sync History
                    </Typography>
                </Box>

                <Paper elevation={2}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Start Time</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Earliest Modified</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Updated</TableCell>
                                    <TableCell align="right">Skipped</TableCell>
                                    <TableCell align="right">Errors</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            {formatDate(row.startTime)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDuration(row.startTime, row.endTime)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.status}
                                                color={row.status === 'success' ? 'success' : 'error'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(row.earliestDateTimeModified)}
                                        </TableCell>
                                        <TableCell align="right">{row.stats.total}</TableCell>
                                        <TableCell align="right">{row.stats.updated}</TableCell>
                                        <TableCell align="right">{row.stats.skipped}</TableCell>
                                        <TableCell align="right">{row.stats.errors}</TableCell>
                                    </TableRow>
                                ))}
                                {history.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No sync history found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50]}
                    />
                </Paper>
            </Box>
        </Container>
    );
};

export default SyncHistory; 